import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Lean PCOS — insulin signaling + androgen handling + low SHBG, without obesity.

export const pcosNina: Scenario = {
  slug: 'pcos-nina',
  series: 'Hormones & Mood',
  title: 'She was told PCOS was about weight. Her insulin told another story.',
  socialHook:
    "Nina, 28, had irregular cycles, jawline acne, chin hairs, and a normal BMI. Her labs looked borderline until insulin and SHBG were read together. The story was not weight. It was insulin plus active testosterone.",
  meta: {
    description:
      "A fictional case study: Nina has irregular cycles, acne, normal BMI, high fasting insulin, low SHBG, and androgen-sensitive genetics. Her story reframes PCOS as an insulin and active-testosterone problem, not a weight problem.",
  },

  persona: {
    name: 'Nina',
    age: 28,
    sex: 'Female',
    ancestry: 'Mexican American',
    occupation: 'Product designer',
    chiefConcern:
      "Her cycles range from 38 to 70 days. She has persistent jawline acne, new chin hairs, and intense carbohydrate cravings in the afternoon. Her BMI is 21.8, so she has repeatedly been told she does not look like a typical PCOS patient. Her ultrasound was described as 'borderline polycystic,' and her total testosterone was high-normal rather than clearly elevated. She wants to know why the pattern feels obvious in her body but ambiguous on paper.",
    familyHistory:
      'Mother: type 2 diabetes diagnosed at 50. Older sister: irregular cycles and gestational diabetes.',
  },

  clinicalPicture: {
    symptoms: [
      'Irregular cycles — usually 38–70 days, occasional skipped periods',
      'Jawline acne flares before delayed periods',
      'New coarse chin hairs requiring weekly removal',
      'Afternoon cravings and energy dips after carbohydrate-heavy lunches',
    ],
    labs: [
      { name: 'Fasting Insulin', value: '16 µIU/mL', refRange: '<10 µIU/mL', flag: 'high' },
      { name: 'HOMA-IR', value: '3.4', refRange: '<2.5', flag: 'high' },
      { name: 'SHBG', value: '24 nmol/L', refRange: '30–120 nmol/L', flag: 'low' },
      { name: 'Total Testosterone', value: '48 ng/dL', refRange: '15–70 ng/dL', flag: 'normal' },
      { name: 'Free Testosterone', value: '6.8 pg/mL', refRange: '0.5–6.4 pg/mL', flag: 'high' },
    ],
    medications: ['No prescription medications currently'],
    supplements: ['Spearmint tea intermittently', 'Magnesium glycinate at night'],
    lifestyle: [
      'BMI 21.8, strength trains once weekly, mostly sedentary workday',
      'Diet: often skips breakfast, larger carbohydrate-heavy lunch',
      'Non-smoker',
      'Alcohol: 1–2 drinks/week',
      'Sleep: 7 hours, worse in the week before a delayed period',
    ],
  },

  genetics: [
    {
      gene: 'INSR',
      variant: 'Insulin receptor sensitivity variant',
      rsid: 'rs2059806',
      genotype: 'Reduced insulin signaling efficiency',
      plainMeaning:
        "Nina can look lean and still run high insulin after meals. Her cells may need more insulin than average to handle the same glucose load.",
      technicalDetail:
        "INSR encodes the insulin receptor. Reduced signaling efficiency means the pancreas must release more insulin to produce the same glucose uptake in muscle and fat tissue. In PCOS, insulin is not just a blood sugar hormone: high insulin directly stimulates ovarian theca cells to produce more androgens and suppresses liver production of SHBG, increasing free testosterone. This is why insulin resistance can drive PCOS symptoms even in a lean body.",
    },
    {
      gene: 'DENND1A',
      variant: 'PCOS-associated ovarian androgen variant',
      rsid: 'rs10818854',
      genotype: 'Risk allele carrier',
      plainMeaning:
        "When insulin and LH signals rise, her ovaries may be more likely to make extra androgens. That helps connect the acne, chin hairs, and delayed ovulation.",
      technicalDetail:
        "DENND1A is one of the strongest replicated PCOS susceptibility loci. Functional studies implicate DENND1A.V2 in ovarian theca-cell androgen production. In a susceptible ovary, insulin and LH signaling amplify androgen output; those androgens then disrupt follicle maturation, delaying ovulation and lengthening cycles. Nina's high-normal total testosterone understated the problem because low SHBG increased the free, biologically active fraction.",
    },
    {
      gene: 'SHBG',
      variant: 'Lower-SHBG tendency',
      rsid: 'rs6259',
      genotype: 'Lower binding capacity pattern',
      plainMeaning:
        "The problem is the active testosterone, not just the total number. Less SHBG leaves more testosterone unbound, so a 'normal' total testosterone can still act too high.",
      technicalDetail:
        "Sex hormone-binding globulin binds testosterone and estradiol in the bloodstream. Insulin suppresses SHBG production in the liver, and SHBG genetic variation can further lower the binding pool. Low SHBG raises free testosterone even when total testosterone remains within the reference range. This is the lab pattern that makes lean PCOS easy to miss: the headline androgen number looks normal, while the active fraction is elevated.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She did not match the stereotype, so the pattern was missed",
      body: "Nina knew something was off. Her periods were unpredictable enough that she stopped trusting calendar apps. Acne clustered along her jaw. The chin hairs were new. But every appointment seemed to circle back to the same objection: she was not overweight. Her ultrasound was borderline, her total testosterone was technically normal, and she was told stress could explain the rest. The advice was familiar: eat well, manage stress, come back if she wanted to conceive. It did not explain why her body felt like it was running a hormone program she could not control.",
    },
    {
      type: 'narrative',
      heading: 'PCOS is not always visible on body size',
      body: "PCOS is often reduced to a picture: weight gain, ovarian cysts, irregular periods. Nina's case shows the mechanism can be hidden. High insulin can tell the ovaries to make more androgen and tell the liver to make less SHBG, the protein that keeps testosterone bound and inactive. The result can be irregular ovulation, acne, hair growth, and cravings even when BMI is normal. Lean PCOS is not mild PCOS. It is PCOS with fewer visual clues.",
    },
    {
      type: 'reveal',
      heading: 'Her glucose looked fine. Her insulin gave the plot away.',
      body: "Nina's fasting glucose looked normal, but her fasting insulin did not. Her pancreas was working harder than it should to keep glucose controlled. That matters because insulin is one of the strongest androgen signals in the ovary. Her INSR variant explains why her cells may require more insulin for the same meal. Her lunch pattern — skipping breakfast, then eating a large carbohydrate-heavy meal — repeatedly pushes the pathway hardest at the point her biology is least forgiving. The glucose number hid the strain. Insulin revealed it.",
    },
    {
      type: 'reveal',
      heading: 'The total testosterone was not the real clue',
      body: "Total testosterone measures everything in circulation: bound and unbound. Only free testosterone is biologically active. Nina's SHBG is low, partly because insulin suppresses it and partly because her genetics point in the same direction. So total testosterone can sit inside the reference range while free testosterone crosses the line. That is the aha: the acne and hair growth were not contradicting the lab. They were pointing to the active fraction the headline number obscured.",
    },
    {
      type: 'action',
      heading: 'Five levers that match the mechanism',
      items: [
        'Track cycle length, acne flares, and cravings together for 8–12 weeks. The pattern matters because delayed ovulation, androgen symptoms, and insulin dips often move together.',
        'Ask for fasting insulin, HOMA-IR, SHBG, free testosterone, LH, FSH, DHEA-S, and prolactin to be reviewed together. PCOS is a pattern diagnosis; single normal values can be misleading.',
        'Discuss inositol support, especially myo-inositol with D-chiro-inositol in a physiologic ratio. Inositols improve insulin signaling in many PCOS patients and are directly aligned with her first domino.',
        'Change meal structure before changing calories: protein-forward breakfast, carbohydrates paired with protein/fat/fiber, and fewer large solo-carb meals. The goal is a lower insulin spike, not weight loss.',
        'Add resistance training 2–3 times weekly. Muscle is the main glucose sink after meals; increasing muscle insulin sensitivity directly lowers the signal driving ovarian androgen output.',
      ],
    },
  ],

  extraBeats: [
    {
      type: 'heatmap' as const,
      heading: "Nina's cycle and androgen symptom map",
      caption:
        'Tracking showed the symptoms were not random: cravings rose first, acne followed, and the delayed period came last. The pattern matched insulin plus active testosterone, not weight.',
      weekLabels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      rows: [
        { symptom: 'Carb cravings', days: [2, 3, 3, 4, 4, 3, 2, 3, 4, 4, 5, 4, 3, 2, 2, 3, 4, 5, 5, 4, 3, 2, 2, 3, 4, 4, 3, 2] },
        { symptom: 'Jawline acne', days: [1, 1, 2, 2, 3, 3, 2, 1, 2, 3, 3, 4, 4, 3, 2, 2, 3, 4, 5, 5, 4, 3, 2, 2, 3, 4, 4, 3] },
        { symptom: 'Energy dip after lunch', days: [3, 3, 4, 4, 4, 3, 2, 3, 4, 5, 5, 4, 3, 2, 3, 3, 4, 5, 5, 4, 3, 3, 2, 3, 4, 4, 3, 2] },
      ],
    },
  ],

  cta: {
    namedPrompt: 'pcos-nina',
    label: 'Explore your PCOS and hormone genetics in Coach',
  },
}
